import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { Loader2, Inbox, MailPlus, ArrowRight } from 'lucide-react';
import { listCampaigns, formatPence, estimateCampaignCostPence } from '../services/campaigns';
import type { Campaign } from '../types';

interface CampaignsScreenProps {
  user: User;
  onOpenCampaign: (id: string) => void;
}

const STATUS_STYLES: Record<Campaign['status'], string> = {
  draft: 'bg-slate-100 text-slate-600',
  scheduled: 'bg-sky-100 text-sky-700',
  sending: 'bg-amber-100 text-amber-700',
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
};

export default function CampaignsScreen({ user, onOpenCampaign }: CampaignsScreenProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await listCampaigns(user.id);
        if (!cancelled) setCampaigns(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <MailPlus size={20} className="text-emerald-600" /> Campaigns
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every prospecting send and what it produced.
          </p>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="animate-spin mx-auto" size={28} />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <Inbox size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">No campaigns yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Head to the Prospect tab to draw a territory and create your first campaign.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Name</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Targets</th>
                  <th className="text-right font-semibold px-4 py-3">Est. cost</th>
                  <th className="text-left font-semibold px-4 py-3">Created</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => onOpenCampaign(c.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.template_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[c.status]}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{c.target_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {formatPence(c.cost_pence ?? estimateCampaignCostPence(c.target_count))}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ArrowRight size={16} className="text-slate-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
